import { useEffect, useRef } from 'react';

import { WorldChatPane } from '../chat';
import { findChannel } from '../config/channels';
import { InspectionPanel } from '../inspect';
import { mountWorldRenderer, type WorldRendererHandle } from '../pixi';

import { ClockHud, WeatherHud } from './ClockHud';
import { useShell } from './state';
import styles from './shell.module.css';

/**
 * Main pane — renders the active channel's content.
 *
 * Three branches:
 *   - world: the PixiJS canvas mount (D5+). P1.7 D4 leaves a placeholder
 *     element that D5 replaces with the renderer.
 *   - bridge (#world-chat): text-pane view of zone chat + /world
 *     broadcasts. D9 fills this in.
 *   - text-placeholder (#general / #random): "Messages coming soon" empty
 *     state per CLIENT_DESIGN §11.3.
 */
export function MainPane(): JSX.Element {
  const activeChannel = useShell((s) => s.activeChannel);
  const channel = findChannel(activeChannel);

  if (!channel) {
    return (
      <main className={styles.mainPane}>
        <header className={styles.mainPaneHeader}>
          <span className={styles.mainPaneTitle}>Unknown channel</span>
        </header>
        <div className={styles.mainPaneBody}>
          <div className={styles.placeholderBody}>
            <div className={styles.placeholderTitle}>Channel not found</div>
            <div className={styles.placeholderDescription}>
              The active channel id doesn&rsquo;t match any known channel.
              Select one from the list.
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.mainPane}>
      <header className={styles.mainPaneHeader}>
        <span className={styles.mainPaneTitle}>{channel.label}</span>
        {channel.description ? (
          <span className={styles.mainPaneHint}>{channel.description}</span>
        ) : null}
      </header>

      <div className={styles.mainPaneBody}>
        {channel.kind === 'text-placeholder' ? (
          <PlaceholderView title={channel.label} />
        ) : channel.kind === 'bridge' ? (
          <BridgePlaceholder />
        ) : (
          <WorldMount />
        )}
      </div>
    </main>
  );
}

function PlaceholderView({ title }: { title: string }): JSX.Element {
  return (
    <div className={styles.placeholderBody}>
      <div className={styles.placeholderTitle}>Messages coming soon</div>
      <div className={styles.placeholderDescription}>
        {title} is a read-only placeholder in Phase 1. Real text-channel
        messaging lands in Phase 2+, once the Gateway gains a text-channel
        persistence layer (CLIENT_DESIGN §11.3).
      </div>
    </div>
  );
}

function BridgePlaceholder(): JSX.Element {
  return <WorldChatPane />;
}

function WorldMount(): JSX.Element {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let handle: WorldRendererHandle | null = null;
    let cancelled = false;

    void (async () => {
      const result = await mountWorldRenderer(host);
      if (cancelled) {
        // React StrictMode unmount fired before the async mount resolved.
        result.destroy();
        return;
      }
      handle = result;
    })();

    return () => {
      cancelled = true;
      if (handle) {
        handle.destroy();
        handle = null;
      }
    };
  }, []);

  return (
    <div ref={hostRef} className={styles.worldMount}>
      <WeatherHud />
      <ClockHud />
      <InspectionPanel />
    </div>
  );
}
